import { Grid, Slider, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import theme from "Theme/AppTheme";
import ServiceHierarchySelect from "../OrganizationModules/fields/ServiceHierarchySelect";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import ControlsContainer from "./ControlsContainer";
import AcxInputWithChips from "components/UI/AcxInputWithChips";

type Props = {
    redactionEnabled: boolean;
    orgRedactionOptions: string[] | null | undefined;
    localActiveOrgRedactionOptions: string[] | null | undefined;
    setLocalActiveOrgRedactionOptions: React.Dispatch<
        React.SetStateAction<string[] | null | undefined>
    >;
    hierarchiesToExtractPII: string[] | null | undefined;
    setHierarchiesToExtractPII: (hierarchyIds: string[]) => void;
    localPersistOriginalTranscript: boolean;
    setLocalPersistOriginalTranscript: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    localExtractPIIEnabled: boolean;
    setLocalExtractPIIEnabled: (checked: boolean) => void;
    PIIOptions: string[] | null | undefined;
    localActivePIIOptions: string[] | undefined | null;
    setLocalActivePIIOptions: React.Dispatch<
        React.SetStateAction<string[] | null | undefined>
    >;
    orgId: string;
    handleModelOptionToggle: (
        checked: boolean,
        modelOptionName: string,
    ) => void;
    voiceRedactionFactor?: number;
    setVoiceRedactionFactor: React.Dispatch<
        React.SetStateAction<number | undefined>
    >;
    isViewOnly: boolean;
    redactHotWords: string[];
    setRedactHotWords: React.Dispatch<React.SetStateAction<string[]>>;
    redactHotWordsIsEnabled: boolean;
    setRedactHotWordsIsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
};

const RedactionModelControls: React.FC<Props> = observer((props: Props) => {
    const heading = "Redaction";
    const description = `Detects and obscures personally identifiable information to
                ensure data privacy and compliance.`;

    return (
        <ControlsContainer
            heading={heading}
            description={description}
            isEnabled={props.redactionEnabled}
            setIsEnabled={(checked) => {
                props.handleModelOptionToggle(checked, "Redaction");
            }}
            maxHeight={650}
            isViewOnly={props.isViewOnly}
        >
            <Grid
                container
                item
                xs={12}
                style={{
                    padding: theme.spacing(1),
                }}
                alignItems="center"
            >
                <>
                    <Grid item xs={12}>
                        <AcxSelectMulti
                            options={
                                props.orgRedactionOptions?.map((optString) => ({
                                    label: optString,
                                    value: optString,
                                })) ?? []
                            }
                            id="redaction-options-select-multi"
                            labelField="label"
                            valueField="value"
                            inputLabel="Redaction entities:"
                            fullWidth
                            containerHeight={"auto"}
                            defaultValue={
                                props.localActiveOrgRedactionOptions?.map(
                                    (optString) => ({
                                        label: optString,
                                        value: optString,
                                    }),
                                ) ?? []
                            }
                            onChange={(val) => {
                                props.setLocalActiveOrgRedactionOptions(
                                    val?.map((item) => item.label),
                                );
                            }}
                            isDisabled={props.isViewOnly}
                        />
                    </Grid>
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems={"center"}
                        paddingTop={theme.spacing(1)}
                    >
                        <AcxSwitch
                            onChange={() => {
                                props.setRedactHotWordsIsEnabled(
                                    (prev) => !prev,
                                );
                                // When this gets disabled, clear out  options
                                if (!props.redactHotWordsIsEnabled) {
                                    props.setRedactHotWords([]);
                                }
                            }}
                            checked={props.redactHotWordsIsEnabled}
                            disabled={props.isViewOnly}
                        />
                        Always Redact Certain Terms
                        {props.redactHotWordsIsEnabled && (
                            <>
                                <Typography>
                                    Enter words or phrases that should always be
                                    redacted from transcripts. Terms are not
                                    case sensitive:
                                </Typography>
                                <AcxInputWithChips
                                    values={props.redactHotWords}
                                    setValues={(values) =>
                                        props.setRedactHotWords(values)
                                    }
                                    sx={{ width: "100%" }}
                                />
                            </>
                        )}
                    </Grid>

                    {props.localActiveOrgRedactionOptions?.includes(
                        "VoiceRedaction",
                    ) && (
                        <Grid item xs={12} sx={{ paddingX: 1 }}>
                            <Grid
                                container
                                item
                                xs={12}
                                alignItems={"center"}
                                paddingTop={theme.spacing(1)}
                            >
                                <Typography
                                    component={"label"}
                                    id="voice-redaction-slider-label"
                                    fontWeight={700}
                                    fontSize={"12px"}
                                    color={theme.palette.gray.main}
                                >
                                    Voice redaction factor:
                                </Typography>
                            </Grid>

                            <Slider
                                value={props.voiceRedactionFactor}
                                onChange={(
                                    _event: any,
                                    newValue: number | number[],
                                ) => {
                                    props.setVoiceRedactionFactor(
                                        newValue as number,
                                    );
                                }}
                                step={0.01}
                                min={-0.1}
                                max={0.1}
                                color="primary"
                                aria-labelledby="voice-redaction-slider-label"
                                valueLabelDisplay="auto"
                                disabled={props.isViewOnly}
                            />
                        </Grid>
                    )}
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems={"center"}
                        paddingTop={theme.spacing(1)}
                    >
                        <b>Extraction: </b>&nbsp;store PII from selected
                        hierarchies
                    </Grid>
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems={"center"}
                        paddingTop={theme.spacing(1)}
                    >
                        <AcxSwitch
                            onChange={(_, checked) => {
                                props.setLocalExtractPIIEnabled(checked);
                            }}
                            checked={props.localExtractPIIEnabled}
                            disabled={props.isViewOnly}
                        />
                        Extract PII
                    </Grid>
                    {props.localExtractPIIEnabled && (
                        <>
                            <Grid item xs={12} paddingTop={theme.spacing(1)}>
                                <AcxSelectMulti
                                    options={
                                        props.PIIOptions?.map((optString) => ({
                                            label: optString,
                                            value: optString,
                                        })) ?? []
                                    }
                                    id="PII-option-select-multi"
                                    labelField="label"
                                    valueField="value"
                                    inputLabel="Extract the following from redacted conversations:"
                                    fullWidth
                                    containerHeight={"auto"}
                                    defaultValue={
                                        props.localActivePIIOptions?.map(
                                            (optString) => ({
                                                label: optString,
                                                value: optString,
                                            }),
                                        ) ?? []
                                    }
                                    onChange={(val) => {
                                        props.setLocalActivePIIOptions(
                                            val?.map((item) => item.label),
                                        );
                                    }}
                                    isDisabled={props.isViewOnly}
                                />
                            </Grid>
                            <Grid item xs={12} paddingTop={theme.spacing(1)}>
                                <ServiceHierarchySelect
                                    maxContainerHeight="200px"
                                    orgId={props.orgId}
                                    value={props.hierarchiesToExtractPII ?? []}
                                    onChange={(val) => {
                                        props.setHierarchiesToExtractPII(val);
                                    }}
                                    fullWidth
                                    label="Extract PII in addition to redacting on these hierarchies:"
                                    placeholder="Select Hierarchies"
                                    isDisabled={props.isViewOnly}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid
                        container
                        item
                        xs={12}
                        alignItems={"center"}
                        paddingTop={theme.spacing(1)}
                    >
                        <Grid item width={"58px"}>
                            <AcxSwitch
                                onChange={() => {
                                    props.setLocalPersistOriginalTranscript(
                                        (prev) => !prev,
                                    );
                                }}
                                checked={props.localPersistOriginalTranscript}
                                disabled={props.isViewOnly}
                            />
                        </Grid>
                        <Grid container direction={"row"} item xs={9}>
                            <Grid item xs={12}>
                                Store unredacted transcript
                            </Grid>
                            <Grid item xs={12} fontSize={"12px"}>
                                Unredacted transcript will not be accessible in
                                the platform
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            </Grid>
        </ControlsContainer>
    );
});

export default RedactionModelControls;
